import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SectionStepper from '../components/SectionStepper';
import QuestionCard from '../components/QuestionCard';
import FinalStep from '../components/FinalStep';
import ScorecardSplash from '../components/ScorecardSplash';
import { fetchQuestionnaire, submitScorecard } from '../api';
import { trackLeadConversion } from '../analytics';
import useNoIndex from '../hooks/useNoIndex';

export default function Scorecard() {
  useNoIndex();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setQuestionnaire(null);
    setLoadError(null);
    setStarted(false);
    setStep(0);
    setAnswers({});
    fetchQuestionnaire(slug)
      .then((data) => { if (!cancelled) setQuestionnaire(data); })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.response?.data?.error || 'This scorecard could not be loaded.');
        }
      });
    return () => { cancelled = true; };
  }, [slug]);

  const questions = questionnaire?.questions || [];

  // One "section" per dimension (in dimension order), each holding its own
  // questions in authoring order. Any question whose dimensionKey doesn't
  // match a dimension is collected into a trailing group so nothing is lost.
  const sections = useMemo(() => {
    if (!questionnaire) return [];
    const dims = [...(questionnaire.dimensions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const byKey = new Map();
    const built = dims.map((d) => {
      const section = { key: d.key, label: d.label, questions: [] };
      byKey.set(d.key, section);
      return section;
    });
    const orphans = { key: '__other', label: 'Additional questions', questions: [] };
    for (const q of questionnaire.questions || []) {
      (byKey.get(q.dimensionKey) || orphans).questions.push(q);
    }
    const result = built.filter((s) => s.questions.length > 0);
    if (orphans.questions.length) result.push(orphans);
    return result;
  }, [questionnaire]);

  const stepperLabels = useMemo(() => [...sections.map((s) => s.label), 'Done'], [sections]);

  const onFinalStep = step === sections.length;
  const currentSection = onFinalStep ? null : sections[step];
  const answeredInSection = currentSection
    ? currentSection.questions.filter((q) => Number.isInteger(answers[q.id])).length
    : 0;
  const sectionComplete = currentSection
    ? answeredInSection === currentSection.questions.length
    : false;

  // Sections can be long — jump back to the top whenever the step changes.
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  function setAnswer(id, value) {
    setAnswers((a) => ({ ...a, [id]: value }));
  }

  function goNext() {
    if (!sectionComplete) return;
    setError(null);
    setStep((s) => s + 1);
  }

  function goBack() {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  }

  async function handleFinalSubmit({ email, context }) {
    // Safety net: make sure every question has an answer before submitting,
    // and jump back to the section holding the first gap if one exists.
    const missing = questions.find((q) => !Number.isInteger(answers[q.id]));
    if (missing) {
      const sectionIndex = sections.findIndex((s) => s.questions.some((q) => q.id === missing.id));
      setError('Some questions were skipped — let’s finish those first.');
      setStep(sectionIndex >= 0 ? sectionIndex : 0);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { token } = await submitScorecard({ slug: questionnaire.slug, answers, email, context });
      trackLeadConversion();
      navigate(`/results/${token}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="page">
        <div className="card">
          <div className="error">{loadError}</div>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="page">
        <div className="card">
          <p className="muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!started) {
    return <ScorecardSplash questionnaire={questionnaire} onStart={() => setStarted(true)} />;
  }

  return (
    <div className="page page--scorecard">
      <SectionStepper steps={stepperLabels} current={step} />

      {onFinalStep && (
        <div className="scorecard-topnav">
          <button type="button" className="secondary" onClick={goBack}>← Back</button>
        </div>
      )}

      <div className="card">
        {onFinalStep ? (
          <FinalStep
            contextFields={questionnaire.contextFields}
            onSubmit={handleFinalSubmit}
            submitting={submitting}
            error={error}
          />
        ) : (
          <>
            {error && <div className="error">{error}</div>}

            <div className="section-head">
              <div className="section-kicker">Section {step + 1} of {sections.length}</div>
              <h2>{currentSection.label}</h2>
            </div>

            <div className="section-questions">
              {currentSection.questions.map((q, i) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  number={i + 1}
                  value={answers[q.id] ?? null}
                  onChange={(v) => setAnswer(q.id, v)}
                />
              ))}
            </div>

            <div className="nav-row section-nav">
              <button type="button" className="secondary" onClick={goBack} disabled={step === 0}>
                ← Back
              </button>
              <button type="button" onClick={goNext} disabled={!sectionComplete}>
                {sectionComplete
                  ? 'Continue →'
                  : `${answeredInSection} / ${currentSection.questions.length} answered`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
